import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

type InlineEditableTextProps = {
  value: string;
  onSave: (nextValue: string) => Promise<void>;
  onError?: (message: string | null) => void;
  disabled?: boolean;
  ariaLabel?: string;
  fontSize?: string | number;
  fontWeight?: number;
  /** Shown (muted) while the value is empty, so an unset field is still clickable. */
  placeholder?: string;
};

const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  value,
  onSave,
  onError,
  disabled = false,
  ariaLabel = 'Inline editable text',
  fontSize = '1.1rem',
  fontWeight = 500,
  placeholder,
}) => {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const rootRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState(0);

  const displayText = editing || saving ? draft : value;

  useLayoutEffect(() => {
    if (measureRef.current) {
      const w = measureRef.current.offsetWidth;
      if (w > 0) setInputWidth(w + 2);
    }
  }, [displayText]);

  useEffect(() => {
    if (!editing && !saving) setDraft(value);
  }, [value, editing, saving]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  useEffect(() => {
    if (!editing || saving) return;
    function handleOutsidePointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        setDraft(value);
        setEditing(false);
        onError?.(null);
      }
    }
    document.addEventListener('mousedown', handleOutsidePointerDown, true);
    document.addEventListener('touchstart', handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown, true);
      document.removeEventListener('touchstart', handleOutsidePointerDown, true);
    };
  }, [editing, saving, value, onError]);

  function handleClick() {
    if (disabled || saving || editing) return;
    setEditing(true);
  }

  function handleCancel() {
    setDraft(value);
    setEditing(false);
    onError?.(null);
  }

  async function handleSave() {
    if (disabled || saving) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value.trim()) {
      handleCancel();
      return;
    }
    setSaving(true);
    setEditing(false);
    onError?.(null);
    try {
      await onSave(trimmed);
    } catch {
      setDraft(value);
    } finally {
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void handleSave();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') handleCancel();
  }

  const sharedTextStyle: React.CSSProperties = {
    fontSize,
    fontWeight,
    fontFamily: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    whiteSpace: 'pre',
  };

  const isMutedText = saving || disabled;

  return (
    <form
      ref={rootRef}
      onSubmit={handleSubmit}
      className="inline-flex w-fit items-center gap-1"
    >
      {/* Hidden span to measure text width */}
      <span
        ref={measureRef}
        aria-hidden="true"
        style={{
          ...sharedTextStyle,
          position: 'fixed',
          visibility: 'hidden',
          top: -9999,
          left: -9999,
          pointerEvents: 'none',
        }}
      >
        {displayText || placeholder || ' '}
      </span>

      <span
        onClick={handleClick}
        className={cn(
          'inline-flex items-center rounded-md px-1.5 py-0.5 transition-[background-color,box-shadow]',
          isMutedText ? 'text-muted-foreground' : 'text-foreground',
          editing ? 'cursor-text' : isMutedText ? 'cursor-default' : 'cursor-pointer',
          !editing && !disabled && !saving && 'hover:bg-muted hover:shadow-[inset_0_0_0_1px_var(--color-border)]',
        )}
      >
        <input
          ref={inputRef}
          value={displayText}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          readOnly={!editing || saving || disabled}
          aria-label={ariaLabel}
          placeholder={placeholder}
          style={{
            ...sharedTextStyle,
            cursor: editing && !saving && !disabled ? 'text' : 'inherit',
            caretColor: editing && !saving && !disabled ? 'auto' : 'transparent',
            userSelect: editing && !saving && !disabled ? 'auto' : 'none',
            color: 'inherit',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: 0,
            margin: 0,
            boxSizing: 'content-box',
            width: inputWidth > 0 ? `${inputWidth}px` : 'auto',
            minWidth: 40,
          }}
        />
      </span>

      {/* Tick / spinner — always reserves space so the layout doesn't jump */}
      <button
        type="submit"
        disabled={saving || !editing}
        aria-label="Сохранить"
        className={cn(
          'inline-flex size-7 items-center justify-center rounded-md text-primary transition-colors',
          'hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          'disabled:opacity-60',
          !editing && !saving && 'invisible',
        )}
      >
        {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
      </button>
    </form>
  );
};

export default InlineEditableText;
